import { Pause, Play } from '@tunasong/icons'
import { Button, IconButton } from '@tunasong/ui-lib'
import type { FC, ReactNode } from 'react'
import styles from './performance-bottom-bar.module.css'

export interface PerformanceBottomBarProps {
  /** Time, on the left: autoscroll and the metronome, or the playlist's audio. */
  children?: ReactNode
  /** The set, at the far right, away from Play (GH #1612). */
  navigation?: ReactNode
  className?: string
}

/** The bottom of the performance layout, at every width: a solid bar in the layout, never over the song. */
export const PerformanceBottomBar: FC<PerformanceBottomBarProps> = props => {
  const { children, navigation, className } = props
  return (
    <div
      role="toolbar"
      aria-label="Performance controls"
      className={[styles.bar, className, 'tuna-hide-print'].filter(Boolean).join(' ')}
    >
      <div className={styles.time}>{children}</div>
      <div className={styles.set}>{navigation}</div>
    </div>
  )
}

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) {
    return '--:--'
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export interface AutoscrollButtonProps {
  isPlaying: boolean
  /** Estimated or inferred duration in seconds */
  duration?: number | null
  onToggle: () => void
  /** Phones: the icon alone */
  compact?: boolean
}

/** "▶ Autoscroll 3:00" */
export const AutoscrollButton: FC<AutoscrollButtonProps> = props => {
  const { isPlaying, duration, onToggle, compact = false } = props
  const time = formatDuration(duration)
  const label = `${isPlaying ? 'Pause' : 'Start'} autoscroll, ${time}`
  const icon = isPlaying ? <Pause /> : <Play />
  if (compact) {
    return (
      <IconButton
        className={styles.target}
        aria-label={label}
        aria-pressed={isPlaying}
        color={isPlaying ? 'secondary' : 'primary'}
        onClick={onToggle}
      >
        {icon}
      </IconButton>
    )
  }
  return (
    <Button
      variant="contained"
      color={isPlaying ? 'secondary' : 'primary'}
      className={[styles.target, styles.autoscroll].join(' ')}
      aria-label={label}
      aria-pressed={isPlaying}
      startIcon={icon}
      onClick={onToggle}
    >
      Autoscroll
      <span className={styles.duration}>{time}</span>
    </Button>
  )
}

export default PerformanceBottomBar
